// NordicLookupsUsagePage — Customer Success view of Nordic lookup usage across
// every customer on a 2026 package (ENG2-2465, T11 of ENG2-2452): who is close
// to their allowance, who is over it, and which companies they used it on.
// Data comes from CMAPI /nordic-lookups/usage (ENG2-2464), which proxies
// RisikaAPI /company/usage/nordic (ENG2-2462).
//
// A Nordic lookup is a distinct Nordic company per customer per contract year;
// the count resets on each customer's anniversary
// (rights.global.nordic_subscription_start_date). Legacy plans are never
// listed. Sorting, paging and search are server-side, like the Global data
// usage page this page is modelled on.

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import {
  faBuilding,
  faChartLine,
  faChevronDown,
  faChevronRight,
  faDatabase,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Topbar } from 'layouts'
import { StatCard } from 'components/ui'
import { useNordicLookupsUsage } from 'services/queries/use-nordic-lookups-usage'
import {
  NordicCompanyUsage,
  NordicLookupsUsageResponse,
} from 'services/cm-api/get-nordic-lookups-usage'
import { planLabel } from 'utils/plan-label'
import { USAGE_BAND_COLOUR, usageBand } from 'utils/usage'
import CustomerLookups from './nordic-lookups-usage/customer-lookups'
import {
  DEFAULT_SORT,
  NordicUsageSort,
  SORT_OPTIONS,
  capLabel,
  daysLeftLabel,
  endsSoon,
  formatDate,
  formatNumber,
} from './nordic-lookups-usage/helpers'

const headerCellSx = {
  py: 1.5,
  px: 2.5,
  bgcolor: 'action.hover',
  borderBottom: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap' as const,
}

const headerTypographySx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
}

const bodyCellSx = {
  py: 1.75,
  px: 2.5,
  borderBottom: '1px solid',
  borderColor: 'divider',
}

const COLUMN_COUNT = 9

const CompanyRow: React.FC<{ company: NordicCompanyUsage }> = ({ company }) => {
  const [expanded, setExpanded] = useState(false)
  const navigate = useNavigate()
  const band = usageBand(company.percent)

  return (
    <>
      <TableRow hover data-testid={`nordic-usage-row-${company.company_id}`}>
        <TableCell sx={{ ...bodyCellSx, width: 40, pr: 0 }}>
          <IconButton
            size="small"
            aria-label={`${expanded ? 'Hide' : 'Show'} companies counted for ${
              company.name
            }`}
            onClick={() => setExpanded((prev) => !prev)}
          >
            <FontAwesomeIcon
              icon={expanded ? faChevronDown : faChevronRight}
              style={{ fontSize: 12 }}
            />
          </IconButton>
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, minWidth: 180 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {company.name}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {company.country} · {company.local_id}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx}>
          <Chip label={planLabel(company.plan)} size="small" />
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Typography sx={{ fontSize: 14 }}>{capLabel(company)}</Typography>
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Typography
            sx={{
              fontSize: 14,
              color: company.over_cap ? 'error.main' : 'text.primary',
              fontWeight: company.over_cap ? 600 : 400,
            }}
          >
            {formatNumber(company.used)}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Typography sx={{ fontSize: 14 }}>
            {company.remaining === null ? '—' : formatNumber(company.remaining)}
          </Typography>
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, minWidth: 180 }}>
          {company.percent === null ? (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Not metered
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(company.percent, 100)}
                aria-label={`${company.percent}% of the cap used`}
                sx={{
                  flex: 1,
                  height: 6,
                  borderRadius: 3,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: USAGE_BAND_COLOUR[band],
                  },
                }}
              />
              <Typography
                sx={{ fontSize: 12, color: 'text.secondary', minWidth: 36 }}
              >
                {company.percent}%
              </Typography>
              {company.over_cap && (
                <Chip label="Over cap" size="small" color="error" />
              )}
            </Box>
          )}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
          <Typography sx={{ fontSize: 14 }}>
            {formatDate(company.period_end)}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: endsSoon(company.days_left)
                ? 'warning.main'
                : 'text.secondary',
            }}
          >
            {daysLeftLabel(company.days_left)}
          </Typography>
        </TableCell>
        <TableCell sx={bodyCellSx} align="right">
          <Button
            size="small"
            variant="outlined"
            aria-label={`Open ${company.name}`}
            onClick={() => navigate(`/customer/${company.company_id}`)}
          >
            Open
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ p: 0, border: 0 }} colSpan={COLUMN_COUNT}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <CustomerLookups customerId={company.company_id} open={expanded} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const SEARCH_DEBOUNCE_MS = 400

const NordicLookupsUsagePage: React.FC = () => {
  const [searchParams] = useSearchParams()
  // ?q=<local id> pre-fills the search (deep link from Usage & limits).
  const initialQuery = (searchParams.get('q') || '').trim()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<NordicUsageSort>(DEFAULT_SORT)
  const [searchInput, setSearchInput] = useState(initialQuery)
  const [search, setSearch] = useState(initialQuery)

  // Debounce the search input so the API is not hit on every keystroke; a
  // new search always starts from page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: response, isLoading } = useNordicLookupsUsage({
    page,
    pageSize,
    q: search || undefined,
    sort,
  })

  const usage: NordicLookupsUsageResponse | null =
    response && response.success ? response.data : null

  // Rollout tolerance, as on the Global data usage page: without a
  // pagination block, show what came back as a single page.
  const pagination = useMemo(
    () =>
      usage?.pagination ?? {
        page: 1,
        page_size: pageSize,
        total: usage?.companies.length ?? 0,
        pages: 1,
      },
    [usage, pageSize],
  )

  return (
    <>
      <Topbar title="Nordic lookups usage" />
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}>
          Unique Nordic companies looked up per customer on the 2026 packages.
          The count resets on each customer&apos;s contract anniversary.
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 8,
              gap: 2,
            }}
          >
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Loading Nordic lookups usage…
            </Typography>
          </Box>
        ) : !usage ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
              Could not load Nordic lookups usage
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Try reloading the page. If the problem persists, check that
              CustomerManagementAPI can reach RisikaAPI.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Customers metered"
                  value={formatNumber(usage.totals.companies)}
                  icon={<FontAwesomeIcon icon={faBuilding} />}
                  iconVariant="blue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Total cap"
                  value={formatNumber(usage.totals.cap)}
                  icon={<FontAwesomeIcon icon={faDatabase} />}
                  iconVariant="blue"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Total used"
                  value={formatNumber(usage.totals.used)}
                  icon={<FontAwesomeIcon icon={faChartLine} />}
                  iconVariant="orange"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} data-testid="over-cap-card">
                <StatCard
                  label="Over cap"
                  value={formatNumber(usage.totals.over_cap)}
                  icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                  iconVariant={usage.totals.over_cap > 0 ? 'red' : 'green'}
                />
              </Grid>
            </Grid>
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                mb: 3,
                minHeight: 18,
              }}
            >
              {usage.totals.unlimited > 0
                ? `${formatNumber(usage.totals.unlimited)} ${
                    usage.totals.unlimited === 1 ? 'customer' : 'customers'
                  } unlimited, not included in the totals`
                : ''}
            </Typography>

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  px: 2.5,
                  py: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>
                  Usage per customer
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  <FormControl
                    size="small"
                    sx={{ minWidth: 170, flex: { xs: 1, md: 'none' } }}
                  >
                    <InputLabel id="nordic-usage-sort-label">
                      Sort by
                    </InputLabel>
                    <Select
                      labelId="nordic-usage-sort-label"
                      id="nordic-usage-sort"
                      label="Sort by"
                      value={sort}
                      onChange={(event) => {
                        setSort(event.target.value as NordicUsageSort)
                        setPage(1)
                      }}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Search all customers by name or local id…"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 } }}
                    inputProps={{ 'aria-label': 'Search customers' }}
                  />
                </Box>
              </Box>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerCellSx, width: 40 }} />
                      {[
                        ['Customer', 'left'],
                        ['Plan', 'left'],
                        ['Cap', 'right'],
                        ['Used', 'right'],
                        ['Remaining', 'right'],
                        ['Utilisation', 'left'],
                        ['Period ends', 'left'],
                        ['', 'right'],
                      ].map(([label, align]) => (
                        <TableCell
                          key={label || 'actions'}
                          sx={headerCellSx}
                          align={align as 'left' | 'right'}
                        >
                          <Typography sx={headerTypographySx}>
                            {label}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usage.companies.length === 0 ? (
                      <TableRow>
                        <TableCell sx={bodyCellSx} colSpan={COLUMN_COUNT}>
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography
                              sx={{ fontSize: 14, color: 'text.secondary' }}
                            >
                              {search
                                ? 'No customers match the search'
                                : 'No customers on metered plans yet'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      usage.companies.map((company) => (
                        <CompanyRow
                          key={company.company_id}
                          company={company}
                        />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={pagination.total}
                page={page - 1}
                onPageChange={(_event, newPage) => setPage(newPage + 1)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(event) => {
                  setPageSize(parseInt(event.target.value, 10))
                  setPage(1)
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                // Left-align the controls, clear of the HubSpot chat bubble
                // (same as the Global data usage page).
                sx={{ '& .MuiTablePagination-spacer': { display: 'none' } }}
              />
            </Paper>
          </>
        )}
      </Box>
    </>
  )
}

export { NordicLookupsUsagePage }
export default NordicLookupsUsagePage
